import React, { useEffect, useMemo, useState } from "react";
import { api, fmtUSD, fmtNumber, fmtDate, fmtRelative } from "../lib/api";
import KpiCard from "../components/KpiCard";
import TMAdvisorPanel from "../components/TMAdvisorPanel";
import TMBestModelsPanel from "../components/TMBestModelsPanel";
import TMPlaygroundPanel from "../components/TMPlaygroundPanel";
import AICostSuitePanel from "../components/AICostSuitePanel";
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from "../components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  ResponsiveContainer, AreaChart, Area, LineChart, Line, BarChart, Bar,
  PieChart, Pie, Cell, CartesianGrid, XAxis, YAxis, Tooltip, Legend, ReferenceLine,
} from "recharts";
import {
  Zap, Brain, Clock, DollarSign, Activity, ShieldAlert,
  CheckCircle2, AlertCircle, AlertOctagon, Database, Layers,
  Cpu, Sparkles, ArrowDownRight, Network,
} from "lucide-react";

const SEVERITY = {
  P0: { color: "#b91c1c", bg: "#fee2e2" },
  P1: { color: "#9a3412", bg: "#ffedd5" },
  P2: { color: "#92400e", bg: "#fef3c7" },
  P3: { color: "#1e40af", bg: "#dbeafe" },
};

const STATUS = {
  healthy:     { color: "#15803d", bg: "#dcfce7", label: "Healthy", icon: CheckCircle2 },
  near_limit:  { color: "#92400e", bg: "#fef3c7", label: "Near Limit", icon: AlertCircle },
  over_budget: { color: "#b91c1c", bg: "#fee2e2", label: "Over Budget", icon: AlertOctagon },
};

const PROVIDER_COLORS = {
  Anthropic: "#d97706",
  Google: "#1a73e8",
  OpenAI: "#10a37f",
  DeepSeek: "#6d28d9",
};

export default function TokenMind() {
  const [days, setDays] = useState(30);
  const [summary, setSummary] = useState(null);
  const [trends, setTrends] = useState([]);
  const [breakdown, setBreakdown] = useState([]);
  const [routing, setRouting] = useState([]);
  const [agents, setAgents] = useState([]);
  const [cache, setCache] = useState([]);
  const [providers, setProviders] = useState([]);

  useEffect(() => {
    Promise.all([
      api.tm.summary(days), api.tm.trends(days), api.tm.breakdown(days),
      api.tm.routing(days), api.tm.agents(days), api.tm.cache(40),
      api.tm.providers(days),
    ]).then(([s, t, b, r, a, c, p]) => {
      setSummary(s); setTrends(t); setBreakdown(b);
      setRouting(r); setAgents(a); setCache(c); setProviders(p);
    }).catch(console.error);
  }, [days]);

  if (!summary) {
    return <div className="px-6 lg:px-10 py-10 text-muted-foreground">Loading TokenMind telemetry…</div>;
  }

  return (
    <div className="px-6 lg:px-10 py-8 space-y-6" data-testid="tokenmind-page">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl cs-gradient-bg flex items-center justify-center">
            <Brain className="w-5 h-5 text-white" />
          </div>
          <div>
            <h2 className="cs-heading text-lg font-semibold tracking-tight">
              ASTRA <span className="cs-gradient-text">TokenMind</span>
            </h2>
            <p className="text-xs text-muted-foreground mt-0.5">
              Universal LLM token optimization engine — provider-agnostic context pruning, semantic caching & intelligent routing
            </p>
          </div>
        </div>
        <Select value={String(days)} onValueChange={(v) => setDays(parseInt(v))}>
          <SelectTrigger className="w-[140px] rounded-full h-9" data-testid="tm-period-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {[7, 14, 30, 60, 90].map(d => (<SelectItem key={d} value={String(d)}>Last {d} days</SelectItem>))}
          </SelectContent>
        </Select>
      </div>

      {/* KPI row — 6 PRD KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <KpiCard testId="tm-kpi-reduction" label="Token Reduction" value={`${summary.token_reduction_pct}%`}
                 sublabel={`${fmtNumber(Math.round(summary.tokens_saved/1e6))}M saved`}
                 icon={ArrowDownRight} tone="primary" />
        <KpiCard testId="tm-kpi-cache" label="Cache Hit Rate" value={`${summary.cache_hit_rate_pct}%`}
                 sublabel={`${fmtNumber(summary.cache_hits)} / ${fmtNumber(summary.cache_requests)}`}
                 icon={Database} tone="success" />
        <KpiCard testId="tm-kpi-latency" label="Latency P95" value={`${summary.latency_overhead_p95_ms}ms`}
                 sublabel={`Target < 150ms`}
                 icon={Clock} tone={summary.latency_overhead_p95_ms < 150 ? "success" : "danger"} />
        <KpiCard testId="tm-kpi-cpi" label="CPI Reduction" value={`${summary.cpi_reduction_pct}%`}
                 sublabel={`$${summary.cpi_optimized_usd} / incident`}
                 icon={DollarSign} tone="primary" />
        <KpiCard testId="tm-kpi-quality" label="Quality Degradation" value={`${summary.quality_degradation_pct}%`}
                 sublabel={`Target < 2%`}
                 icon={CheckCircle2} tone={summary.quality_degradation_pct < 2 ? "success" : "danger"} />
        <KpiCard testId="tm-kpi-breaches" label="Runaway Breaches" value={fmtNumber(summary.quota_breaches)}
                 sublabel={`${summary.quota_alerts} quota alerts`}
                 icon={ShieldAlert} tone={summary.quota_breaches ? "danger" : "success"} />
      </div>

      {/* Cost saved banner */}
      <div className="cs-card p-5 bg-gradient-to-r from-[#00579e]/8 to-[#48b1e4]/8 border-[#00579e]/20" data-testid="tm-savings-banner">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <Sparkles className="w-5 h-5 text-[#00579e]" />
            <div>
              <div className="text-[10px] uppercase tracking-[0.16em] text-[#00579e] font-semibold">Token spend saved · last {days} days</div>
              <div className="cs-kpi-value text-3xl text-foreground mt-0.5">{fmtUSD(summary.cost_saved_usd)}</div>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-6 text-xs">
            <div>
              <div className="text-muted-foreground uppercase tracking-[0.1em]">Baseline</div>
              <div className="cs-mono font-semibold text-lg mt-0.5">{fmtUSD(summary.cost_baseline_usd)}</div>
            </div>
            <div>
              <div className="text-muted-foreground uppercase tracking-[0.1em]">Optimized</div>
              <div className="cs-mono font-semibold text-lg mt-0.5 text-[#15803d]">{fmtUSD(summary.cost_optimized_usd)}</div>
            </div>
            <div>
              <div className="text-muted-foreground uppercase tracking-[0.1em]">Incidents</div>
              <div className="cs-mono font-semibold text-lg mt-0.5">{fmtNumber(summary.incidents_processed)}</div>
            </div>
          </div>
        </div>
      </div>

      <Tabs defaultValue="overview" className="space-y-4">
        <TabsList className="bg-white border border-border rounded-full p-1 h-auto">
          {[
            { v: "overview", l: "Overview" },
            { v: "playground", l: "Routing Playground" },
            { v: "routing", l: "Intelligent Routing" },
            { v: "agents", l: "Agent Budgets" },
            { v: "cache", l: "Semantic Cache" },
            { v: "best-models", l: "Best Models" },
            { v: "ai-cost", l: "AI Cost Suite (GCP)" },
            { v: "advisor", l: "Migration & Advisor" },
          ].map(t => (
            <TabsTrigger key={t.v} value={t.v} data-testid={`tm-tab-${t.v}`}
              className="rounded-full data-[state=active]:bg-[#00579e] data-[state=active]:text-white text-xs px-4 py-1.5">
              {t.l}
            </TabsTrigger>
          ))}
        </TabsList>

        {/* PLAYGROUND */}
        <TabsContent value="playground">
          <TMPlaygroundPanel />
        </TabsContent>

        {/* OVERVIEW */}
        <TabsContent value="overview" className="space-y-4">
          {/* Token trend */}
          <div className="cs-card p-6" data-testid="tm-trend-chart">
            <div className="flex items-center justify-between mb-3">
              <div>
                <h3 className="cs-heading text-base font-semibold tracking-tight">Token Consumption — Baseline vs Optimized</h3>
                <p className="text-xs text-muted-foreground mt-0.5">Daily totals across all 16 ASTRA agents</p>
              </div>
            </div>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={trends} margin={{ top: 10, right: 20, left: 10, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
                  <XAxis dataKey="date" tick={{ fontSize: 10 }} stroke="#6b7280" interval="preserveStartEnd" minTickGap={40}
                         tickFormatter={(d) => fmtDate(d+"T00:00:00Z").split(",")[0]} />
                  <YAxis tick={{ fontSize: 10 }} stroke="#6b7280" tickFormatter={(v) => `${Math.round(v/1e6)}M`} />
                  <Tooltip contentStyle={{ borderRadius: 8, fontSize: 12 }} formatter={(v, n) => [`${fmtNumber(v)} tokens`, n]} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Area type="monotone" dataKey="baseline_tokens" name="Baseline" stroke="#9a3412" strokeWidth={2} fill="#9a3412" fillOpacity={0.18} />
                  <Area type="monotone" dataKey="optimized_tokens" name="Optimized" stroke="#00579e" strokeWidth={2.5} fill="#00579e" fillOpacity={0.28} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* Breakdown donut */}
            <div className="cs-card p-5" data-testid="tm-breakdown-donut">
              <h3 className="cs-heading text-base font-semibold tracking-tight">Where Savings Come From</h3>
              <p className="text-xs text-muted-foreground mt-0.5 mb-3">Optimization technique contribution</p>
              <div className="h-48">
                <ResponsiveContainer>
                  <PieChart>
                    <Pie data={breakdown} dataKey="share_pct" nameKey="label" innerRadius={42} outerRadius={70} paddingAngle={2}>
                      {breakdown.map((d, i) => (<Cell key={i} fill={d.color} />))}
                    </Pie>
                    <Tooltip contentStyle={{ borderRadius: 8, fontSize: 12 }} formatter={(v, n, p) => [`${v}% · ${fmtUSD(p.payload.dollars_saved)}`, n]} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="space-y-1.5 mt-3">
                {breakdown.map(b => (
                  <div key={b.id} className="flex items-center gap-2 text-xs">
                    <span className="w-2 h-2 rounded-sm shrink-0" style={{ background: b.color }} />
                    <span className="text-foreground flex-1">{b.label}</span>
                    <span className="cs-mono text-muted-foreground">{b.share_pct}%</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Provider mix */}
            <div className="cs-card p-5" data-testid="tm-providers-card">
              <h3 className="cs-heading text-base font-semibold tracking-tight">Provider Mix (after routing)</h3>
              <p className="text-xs text-muted-foreground mt-0.5 mb-3">LLM calls per provider</p>
              <div className="h-48">
                <ResponsiveContainer>
                  <BarChart data={providers} layout="vertical" margin={{ left: 8 }}>
                    <XAxis type="number" tick={{ fontSize: 10 }} stroke="#6b7280" tickFormatter={(v) => `${Math.round(v/1000)}k`} />
                    <YAxis dataKey="provider" type="category" tick={{ fontSize: 11 }} stroke="#6b7280" width={70} />
                    <Tooltip contentStyle={{ borderRadius: 8, fontSize: 12 }} formatter={(v) => fmtNumber(v) + " calls"} />
                    <Bar dataKey="calls" radius={[0, 4, 4, 0]} maxBarSize={22}>
                      {providers.map((d, i) => (<Cell key={i} fill={PROVIDER_COLORS[d.provider] || "#6b7280"} />))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <div className="grid grid-cols-2 gap-1.5 mt-3 text-xs">
                {providers.map(p => (
                  <div key={p.provider} className="flex items-center justify-between">
                    <span className="flex items-center gap-1.5">
                      <span className="w-2 h-2 rounded-sm" style={{ background: PROVIDER_COLORS[p.provider] || "#6b7280" }} />
                      {p.provider}
                    </span>
                    <span className="cs-mono text-muted-foreground">{fmtUSD(p.cost_usd)}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Latency trend */}
            <div className="cs-card p-5" data-testid="tm-latency-chart">
              <h3 className="cs-heading text-base font-semibold tracking-tight">Latency Overhead</h3>
              <p className="text-xs text-muted-foreground mt-0.5 mb-3">P95 added round-trip · target &lt;150ms</p>
              <div className="h-48">
                <ResponsiveContainer>
                  <LineChart data={trends}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
                    <XAxis dataKey="date" tick={{ fontSize: 9 }} stroke="#6b7280" tickFormatter={(d) => d.slice(5)} />
                    <YAxis tick={{ fontSize: 10 }} stroke="#6b7280" domain={[60, 180]} />
                    <Tooltip contentStyle={{ borderRadius: 8, fontSize: 12 }} formatter={(v) => `${v}ms`} />
                    <ReferenceLine y={150} stroke="#ef4444" strokeDasharray="4 4" label={{ value: "150ms SLA", fill: "#ef4444", fontSize: 10 }} />
                    <Line type="monotone" dataKey="latency_overhead_p95_ms" stroke="#00579e" strokeWidth={2} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        </TabsContent>

        {/* ROUTING */}
        <TabsContent value="routing">
          <div className="cs-card overflow-hidden" data-testid="tm-routing-card">
            <div className="px-6 py-4 border-b border-border">
              <h3 className="cs-heading text-base font-semibold tracking-tight">Intelligent Routing Decisions</h3>
              <p className="text-xs text-muted-foreground mt-0.5">Task category → routed model. Frontier reserved for P0/P1 complex causal RCA.</p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[hsl(var(--cs-tint))] border-b border-border">
                  <tr className="text-left">
                    <th className="px-4 py-3 text-[10px] uppercase tracking-[0.14em] font-medium text-muted-foreground">Task Category</th>
                    <th className="px-4 py-3 text-[10px] uppercase tracking-[0.14em] font-medium text-muted-foreground">Routed Model</th>
                    <th className="px-4 py-3 text-[10px] uppercase tracking-[0.14em] font-medium text-muted-foreground">vs Baseline</th>
                    <th className="px-4 py-3 text-[10px] uppercase tracking-[0.14em] font-medium text-muted-foreground text-right">Calls</th>
                    <th className="px-4 py-3 text-[10px] uppercase tracking-[0.14em] font-medium text-muted-foreground text-right">Tokens</th>
                    <th className="px-4 py-3 text-[10px] uppercase tracking-[0.14em] font-medium text-muted-foreground text-right">Baseline $</th>
                    <th className="px-4 py-3 text-[10px] uppercase tracking-[0.14em] font-medium text-muted-foreground text-right">Routed $</th>
                    <th className="px-4 py-3 text-[10px] uppercase tracking-[0.14em] font-medium text-muted-foreground text-right">Saved</th>
                    <th className="px-4 py-3 text-[10px] uppercase tracking-[0.14em] font-medium text-muted-foreground text-right">%</th>
                  </tr>
                </thead>
                <tbody>
                  {routing.map((r, i) => (
                    <tr key={i} className="cs-row-hover border-b border-border last:border-0" data-testid={`routing-row-${i}`}>
                      <td className="px-4 py-3">
                        <div className="font-medium text-foreground">{r.task_category}</div>
                        <div className="text-[11px] text-muted-foreground mt-0.5 max-w-md truncate">{r.examples}</div>
                      </td>
                      <td className="px-4 py-3">
                        <span className="cs-pill bg-[#dbeafe] text-[#00579e]">{r.routed_model}</span>
                      </td>
                      <td className="px-4 py-3 text-xs text-muted-foreground">{r.baseline_model}</td>
                      <td className="px-4 py-3 text-right cs-mono text-xs">{fmtNumber(r.calls)}</td>
                      <td className="px-4 py-3 text-right cs-mono text-xs">{fmtNumber(Math.round(r.tokens/1e6))}M</td>
                      <td className="px-4 py-3 text-right cs-mono text-xs text-muted-foreground">{fmtUSD(r.baseline_cost_usd)}</td>
                      <td className="px-4 py-3 text-right cs-mono text-xs">{fmtUSD(r.routed_cost_usd)}</td>
                      <td className="px-4 py-3 text-right cs-mono font-semibold text-[#15803d]">{fmtUSD(r.saved_usd)}</td>
                      <td className="px-4 py-3 text-right">
                        <span className={`cs-pill ${r.savings_pct > 0 ? "bg-[#dcfce7] text-[#15803d]" : "bg-[#f3f4f6] text-[#6b7280]"}`}>{r.savings_pct}%</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>

        {/* AGENTS */}
        <TabsContent value="agents">
          <div className="cs-card overflow-hidden" data-testid="tm-agents-card">
            <div className="px-6 py-4 border-b border-border">
              <h3 className="cs-heading text-base font-semibold tracking-tight">Per-Agent Token Budgets</h3>
              <p className="text-xs text-muted-foreground mt-0.5">16-agent ASTRA pipeline · budget utilization across {summary.incidents_processed} incidents</p>
            </div>
            <div className="divide-y divide-border">
              {agents.map(a => {
                const st = STATUS[a.status] || STATUS.healthy;
                const Icon = st.icon;
                return (
                  <div key={a.id} className="px-6 py-3 flex items-center gap-4" data-testid={`agent-row-${a.id}`}>
                    <div className="w-2 h-8 rounded-sm" style={{ background: a.color }} />
                    <div className="w-56 min-w-0">
                      <div className="font-medium text-foreground text-sm truncate">{a.name}</div>
                      <div className="text-[10px] uppercase tracking-[0.12em] text-muted-foreground">{a.role}</div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex justify-between text-[11px] mb-1">
                        <span className="text-muted-foreground">
                          {fmtNumber(a.consumed_total)} / {fmtNumber(a.budget_total)} tokens
                        </span>
                        <span className="cs-mono font-medium">{a.utilization_pct}%</span>
                      </div>
                      <div className="h-2 w-full bg-[hsl(var(--cs-tint))] rounded-full overflow-hidden">
                        <div className="h-full rounded-full" style={{
                          width: `${Math.min(a.utilization_pct, 100)}%`,
                          background: a.utilization_pct >= 100 ? "#ef4444" : a.utilization_pct >= 85 ? "#f59e0b" : "#22c55e"
                        }} />
                      </div>
                    </div>
                    <div className="text-right w-24 shrink-0">
                      <div className="cs-mono text-xs">{fmtNumber(a.avg_tokens_per_incident)}</div>
                      <div className="text-[10px] text-muted-foreground">avg / incident</div>
                    </div>
                    <span className="cs-pill shrink-0" style={{ color: st.color, background: st.bg, borderColor: st.color + "33" }}>
                      <Icon className="w-3 h-3" /> {st.label}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </TabsContent>

        {/* CACHE */}
        <TabsContent value="cache">
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-4">
            <div className="cs-card overflow-hidden" data-testid="tm-cache-stream">
              <div className="px-6 py-4 border-b border-border flex items-center justify-between">
                <div>
                  <h3 className="cs-heading text-base font-semibold tracking-tight">Semantic Cache — live stream</h3>
                  <p className="text-xs text-muted-foreground mt-0.5">{">"}95% cosine similarity → cache hit · MongoDB Atlas Vector Search</p>
                </div>
                <span className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">Last {cache.length} events</span>
              </div>
              <div className="max-h-[520px] overflow-y-auto">
                <table className="w-full text-xs">
                  <thead className="bg-[hsl(var(--cs-tint))] border-b border-border sticky top-0">
                    <tr className="text-left">
                      <th className="px-4 py-2 font-medium text-muted-foreground uppercase tracking-[0.12em] text-[10px]">Time</th>
                      <th className="px-4 py-2 font-medium text-muted-foreground uppercase tracking-[0.12em] text-[10px]">Event</th>
                      <th className="px-4 py-2 font-medium text-muted-foreground uppercase tracking-[0.12em] text-[10px]">Class</th>
                      <th className="px-4 py-2 font-medium text-muted-foreground uppercase tracking-[0.12em] text-[10px]">Prompt</th>
                      <th className="px-4 py-2 font-medium text-muted-foreground uppercase tracking-[0.12em] text-[10px] text-right">Similarity</th>
                      <th className="px-4 py-2 font-medium text-muted-foreground uppercase tracking-[0.12em] text-[10px] text-right">Tokens saved</th>
                    </tr>
                  </thead>
                  <tbody>
                    {cache.map((c, i) => (
                      <tr key={i} className="border-t border-border" data-testid={`cache-row-${i}`}>
                        <td className="px-4 py-2 cs-mono text-[11px] text-muted-foreground whitespace-nowrap">{fmtRelative(c.timestamp)}</td>
                        <td className="px-4 py-2">
                          <span className={`cs-pill ${c.event_type === "hit" ? "bg-[#dcfce7] text-[#15803d]" : "bg-[#f3f4f6] text-[#6b7280]"}`}>
                            {c.event_type}
                          </span>
                        </td>
                        <td className="px-4 py-2 cs-mono text-[11px]">{c.query_class}</td>
                        <td className="px-4 py-2 text-muted-foreground max-w-md truncate">{c.prompt_preview}</td>
                        <td className="px-4 py-2 text-right cs-mono">{c.similarity}</td>
                        <td className="px-4 py-2 text-right cs-mono font-medium" style={{ color: c.tokens_saved ? "#15803d" : "#9ca3af" }}>
                          {c.tokens_saved ? fmtNumber(c.tokens_saved) : "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Cache TTL config (info card) */}
            <div className="cs-card p-5 space-y-4" data-testid="tm-cache-config">
              <div>
                <h3 className="cs-heading text-base font-semibold tracking-tight">Cache hit rate</h3>
                <div className="cs-kpi-value text-3xl text-[#00579e] mt-2">{summary.cache_hit_rate_pct}%</div>
                <p className="text-xs text-muted-foreground mt-1">PRD target: &gt;40% for repetitive classification</p>
              </div>
              <div className="border-t border-border pt-4 space-y-2">
                <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">ASTRA-aware TTL</div>
                <div className="flex justify-between text-xs"><span>REI cost estimates</span><span className="cs-mono">5m</span></div>
                <div className="flex justify-between text-xs"><span>Classification results</span><span className="cs-mono">1h</span></div>
                <div className="flex justify-between text-xs"><span>Runbook summaries</span><span className="cs-mono">24h</span></div>
              </div>
              <div className="border-t border-border pt-4">
                <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground mb-1.5">Invalidation hooks</div>
                <ul className="text-xs text-foreground space-y-1.5">
                  <li className="flex gap-2"><span className="text-[#00579e]">▸</span><span><span className="cs-mono">incident_resolved</span> → flush incident cache</span></li>
                  <li className="flex gap-2"><span className="text-[#00579e]">▸</span><span><span className="cs-mono">topology_updated</span> → flush blast-radius cache</span></li>
                </ul>
              </div>
            </div>
          </div>
        </TabsContent>

        {/* BEST MODELS — TIPE-style analyzer */}
        <TabsContent value="best-models" className="space-y-6">
          <TMBestModelsPanel />
        </TabsContent>

        {/* AI COST SUITE — GCP AI spend, idle capacity, PTU sim, governance */}
        <TabsContent value="ai-cost" className="space-y-6">
          <AICostSuitePanel />
        </TabsContent>

        {/* MIGRATION & ADVISOR */}
        <TabsContent value="advisor" className="space-y-6">
          <TMAdvisorPanel />
        </TabsContent>
      </Tabs>
    </div>
  );
}
